import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  CheckCircleIcon,
  PencilIcon,
  ChartBarIcon,
  UserCircleIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import moment from 'moment';

// --- START: MOCK DATA ---
const mockEmployees = [
  { id: 1, name: 'Aditya Verma' },
  { id: 2, name: 'Pooja Singh' },
  { id: 3, name: 'Kunal Kapoor' },
];

const mockGoals = [
  { id: 1, title: 'Complete Q3 Project Report', assignedTo: 'Aditya Verma', dueDate: '2025-10-31', status: 'In Progress', progress: 75 },
  { id: 2, title: 'Learn Advanced SQL Queries', assignedTo: 'Pooja Singh', dueDate: '2025-11-15', status: 'Not Started', progress: 0 },
  { id: 3, title: 'Onboard 5 New Clients', assignedTo: 'Kunal Kapoor', dueDate: '2025-10-31', status: 'Completed', progress: 100 },
];
// --- END: MOCK DATA ---

const GoalsTracking = () => {
  const [goals, setGoals] = useState(mockGoals);
  const [newGoal, setNewGoal] = useState({
    title: '',
    assignedToId: '',
    dueDate: '',
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setNewGoal(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateGoal = (e) => {
    e.preventDefault();
    if (!newGoal.title || !newGoal.assignedToId || !newGoal.dueDate) {
      alert('Please fill out all fields.');
      return;
    }
    const assignedToName = mockEmployees.find(emp => emp.id === parseInt(newGoal.assignedToId))?.name;
    const goal = {
      id: goals.length + 1,
      title: newGoal.title,
      assignedTo: assignedToName,
      dueDate: newGoal.dueDate,
      status: 'Not Started',
      progress: 0,
    };
    setGoals([goal, ...goals]);
    setNewGoal({ title: '', assignedToId: '', dueDate: '' });
  };

  const updateGoalStatus = (id, newStatus, newProgress) => {
    setGoals(goals.map(goal =>
      goal.id === id ? { ...goal, status: newStatus, progress: newProgress } : goal
    ));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-600';
      case 'In Progress': return 'bg-yellow-100 text-yellow-600';
      case 'Not Started': return 'bg-red-100 text-red-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const filteredGoals = useMemo(() => {
    let filtered = goals.filter(goal =>
      goal.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      goal.assignedTo.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (statusFilter !== 'All') {
      filtered = filtered.filter(goal => goal.status === statusFilter);
    }
    return filtered;
  }, [searchTerm, statusFilter, goals]);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-200 p-8 text-gray-800">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto mb-8"
      >
        <h1 className="text-4xl font-extrabold text-blue-800">Goals & KPI Tracking</h1>
        <p className="text-xl font-light text-blue-600 mt-2">
          Monitor and manage team and individual goals.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
        {/* Goal Creation Form */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="lg:col-span-1 bg-white p-6 rounded-3xl shadow-lg h-full flex flex-col"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Assign a New Goal</h3>
          <form onSubmit={handleCreateGoal} className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">Goal Title</label>
              <input
                type="text"
                name="title"
                value={newGoal.title}
                onChange={handleFormChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="e.g., Increase sales by 15%"
                required
              />
            </div>
            <div>
              <label htmlFor="assignedToId" className="block text-sm font-medium text-gray-700">Assigned To</label>
              <select
                name="assignedToId"
                value={newGoal.assignedToId}
                onChange={handleFormChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              >
                <option value="">Select Employee</option>
                {mockEmployees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700">Due Date</label>
              <input
                type="date"
                name="dueDate"
                value={newGoal.dueDate}
                onChange={handleFormChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
            <button
              type="submit"
              className="mt-6 w-full flex justify-center items-center py-3 px-4 rounded-full shadow-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Assign Goal
            </button>
          </form>
        </motion.div>

        {/* Goals Tracking Table */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.2 }}
          className="lg:col-span-2 bg-white p-6 rounded-3xl shadow-lg h-full flex flex-col"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-bold text-gray-900">Active Goals</h3>
            <div className="flex space-x-2">
              {['All', 'Not Started', 'In Progress', 'Completed'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`py-2 px-4 rounded-full text-sm font-medium transition-colors
                    ${statusFilter === status ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
          <div className="relative mb-4">
            <input
              type="text"
              placeholder="Search goals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-4 py-2 rounded-full bg-gray-100 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="overflow-x-auto flex-1">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Goal Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                  <th className="relative px-6 py-3 text-right"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <AnimatePresence>
                  {filteredGoals.length > 0 ? (
                    filteredGoals.map((goal) => (
                      <motion.tr
                        key={goal.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{goal.title}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{goal.assignedTo}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{moment(goal.dueDate).format('MMM D, YYYY')}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(goal.status)}`}>
                            {goal.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="w-24 bg-gray-200 rounded-full h-2.5">
                            <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${goal.progress}%` }}></div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => updateGoalStatus(goal.id, 'Completed', 100)}
                              className="p-1 text-green-600 hover:text-green-800"
                              aria-label="Mark as Complete"
                            >
                              <CheckCircleIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => alert(`Editing goal: ${goal.title}`)}
                              className="p-1 text-blue-600 hover:text-blue-800"
                              aria-label="Edit Goal"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="py-10 text-center text-gray-500">
                        No matching goals found.
                      </td>
                    </tr>
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GoalsTracking;